import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { CinematicPalette } from './themePalettes';

const EASE = [0.22, 0.61, 0.36, 1] as const;
const PHOTO_HOLD_MS = 5000;
const FADE_SECONDS = 0.9;

interface CinematicPhotoSequenceProps {
  photoUrls?: string[];
  palette: CinematicPalette;
  onComplete: () => void;
}

export function CinematicPhotoSequence({ photoUrls, palette, onComplete }: CinematicPhotoSequenceProps) {
  const photos = useMemo(() => (photoUrls ?? []).filter(Boolean), [photoUrls]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [failed, setFailed] = useState<Set<string>>(() => new Set());

  const usablePhotos = useMemo(
    () => photos.filter((url) => !failed.has(url)),
    [photos, failed],
  );

  const complete = useCallback(() => {
    setPaused(false);
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    if (photos.length === 0 || usablePhotos.length === 0) {
      complete();
    }
  }, [complete, photos.length, usablePhotos.length]);

  useEffect(() => {
    if (paused || usablePhotos.length === 0) return;
    const timer = setTimeout(() => {
      if (activeIndex >= usablePhotos.length - 1) {
        complete();
        return;
      }
      setActiveIndex((index) => Math.min(index + 1, usablePhotos.length - 1));
    }, PHOTO_HOLD_MS);
    return () => clearTimeout(timer);
  }, [activeIndex, complete, paused, usablePhotos.length]);

  useEffect(() => {
    if (activeIndex > usablePhotos.length - 1) {
      setActiveIndex(Math.max(usablePhotos.length - 1, 0));
    }
  }, [activeIndex, usablePhotos.length]);

  const activePhoto = usablePhotos[activeIndex];
  if (!activePhoto) return null;

  return (
    <motion.section
      className="fixed inset-0 z-[60] overflow-hidden bg-background"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: EASE }}
      aria-label="Cinematic photo reveal"
    >
      <AnimatePresence mode="wait">
        <motion.button
          type="button"
          key={activePhoto}
          className="absolute inset-0 block h-full w-full cursor-pointer"
          onClick={() => setPaused((value) => !value)}
          aria-label={paused ? 'Resume photo reveal' : 'Pause photo reveal'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: FADE_SECONDS, ease: EASE }}
        >
          {/* Blurred backdrop so letterboxed portrait/landscape photos never cut faces */}
          <img
            src={activePhoto}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover blur-2xl scale-110 opacity-50"
          />
          <motion.img
            src={activePhoto}
            alt=""
            className="absolute inset-0 h-full w-full object-contain"
            loading="eager"
            decoding="async"
            initial={{ scale: 1.01, x: '-0.5%', y: '0.5%' }}
            animate={{ scale: 1.06, x: '0.5%', y: '-0.5%' }}
            transition={{ duration: 5.8, ease: 'easeInOut' }}
            onError={() => {
              setFailed((current) => new Set(current).add(activePhoto));
              setActiveIndex((index) => Math.max(index - 1, 0));
            }}
          />
        </motion.button>
      </AnimatePresence>

      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--background)/0)_28%,hsl(var(--background)/0.55)_100%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,hsl(var(--background)/0.28),hsl(var(--background)/0)_30%,hsl(var(--background)/0.58))]" />
      <motion.div
        key={activePhoto}
        className="pointer-events-none absolute inset-x-6 bottom-[calc(env(safe-area-inset-bottom)+2rem)] h-px origin-left"
        style={{ background: palette.accent }}
        initial={{ scaleX: 0, opacity: 0.55 }}
        animate={{ scaleX: 1, opacity: paused ? 0.25 : 0.65 }}
        transition={{ duration: 5, ease: 'linear' }}
      />

      <button
        type="button"
        onClick={complete}
        className="absolute right-4 top-[calc(env(safe-area-inset-top)+1rem)] z-10 rounded-full bg-background/30 px-3 py-2 text-xs uppercase tracking-[0.22em] text-primary-foreground/70 backdrop-blur-md transition-colors hover:text-primary-foreground"
      >
        Skip
      </button>

      <div className="pointer-events-none absolute bottom-[calc(env(safe-area-inset-bottom)+2.75rem)] left-0 right-0 z-10 flex justify-center px-6">
        <motion.p
          className="max-w-[28ch] text-center font-serif text-sm text-primary-foreground/78"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: paused ? 0.95 : 0.75, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          {paused ? 'Paused — tap to continue' : 'Tap to pause'}
        </motion.p>
      </div>
    </motion.section>
  );
}